import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";

// Enum options extracted from Ruby code - using integer values as in backend
export const extractTypeOptions = [
  { value: "0", label: "Csv" },
  { value: "1", label: "Aqr" },
  { value: "2", label: "Ecb" },
  { value: "3", label: "Html" },
  { value: "4", label: "Json" },
  { value: "5", label: "Mosexc" },
  { value: "6", label: "Multicolumn" },
  { value: "8", label: "Txt" },
  { value: "10", label: "Lgim" },
  { value: "11", label: "Unstack" },
  { value: "12", label: "Statestreet Pcf" },
  { value: "13", label: "Unstack Vigeo" },
  { value: "14", label: "Ignore File Content" },
  { value: "15", label: "Xml" },
  { value: "16", label: "Vigeo Sfdr" },
];

export const transformTypeOptions = [
  { value: "0", label: "Generic" },
  { value: "1", label: "Aqr" },
  { value: "7", label: "Ecb" },
  { value: "10", label: "Mvis" },
  { value: "17", label: "Python Script" },
  { value: "19", label: "Tpt V5" },
  { value: "20", label: "Bpss Pos" },
  { value: "21", label: "Resinv" },
  { value: "22", label: "Rc Socgen" },
  { value: "23", label: "Caceis Bank Statement" },
  { value: "24", label: "Deka Pos" },
  { value: "25", label: "Ftg Pos" },
  { value: "26", label: "N1 Transaction" },
  { value: "27", label: "Socgen Transaction" },
  { value: "28", label: "Ept" },
  { value: "29", label: "Emt" },
  { value: "30", label: "Covip" },
  { value: "31", label: "Lazard Pos" },
  { value: "32", label: "Lazard Fixings" },
  { value: "33", label: "Rothschild Fixings" },
  { value: "34", label: "A5 Lazard Transaction" },
  { value: "35", label: "A5 Lombard Transaction" },
  { value: "36", label: "A5 Rothschild Transaction" },
  { value: "37", label: "L1 Rothschild Mm Pos" },
  { value: "38", label: "L1 Rothschild Mm Fixings" },
  { value: "39", label: "L1 Hottinguer Pos" },
  { value: "40", label: "L1 Hottinguer Fixings" },
  { value: "41", label: "L1 Nobc Pos" },
  { value: "42", label: "L1 Nobc Fixings" },
  { value: "43", label: "A6 Lazard Transaction" },
  { value: "44", label: "N1 Transaction New" },
  { value: "45", label: "Oddo France Pos" },
  { value: "46", label: "N1 Time Series" },
];

export const loadTypeOptions = [
  { value: "0", label: "Time Series" },
  { value: "1", label: "Holding Constituents" },
  { value: "2", label: "Versions" },
  { value: "3", label: "Constituents" },
  { value: "4", label: "Admin Data" },
  { value: "5", label: "Portfolio Time Series" },
  { value: "6", label: "Aggregations" },
  { value: "7", label: "Time Serie Files" },
  { value: "8", label: "Pdf Factsheet" },
  { value: "9", label: "Transactions" },
  { value: "10", label: "Time Serie File Dates" },
  { value: "11", label: "Esg Exclusions" },
  { value: "12", label: "C8 Transactions" },
  { value: "13", label: "A5 Transactions" },
  { value: "14", label: "A6 Transactions" },
  { value: "15", label: "L1 Transactions" },
  { value: "16", label: "L1 Time Series" },
  { value: "17", label: "A5 Time Series" },
  { value: "18", label: "C8 Time Series" },
  { value: "19", label: "A6 Time Series" },
  { value: "20", label: "N1 Transactions" },
  { value: "21", label: "N1 Time Series" },
];

export const statusOptions = [
  { value: "0", label: "Current" },
  { value: "1", label: "Deprecated" },
  { value: "2", label: "Draft" },
  { value: "3", label: "Client Service" },
  { value: "4", label: "IT Investigation" },
];

export const frequencyOptions = [
  { value: "1", label: "Daily" },
  { value: "7", label: "Weekly" },
  { value: "30", label: "Monthly" },
  { value: "90", label: "Quarter" },
  { value: "183", label: "Bi Annual" },
  { value: "365", label: "Annually" },
];

export const docTypeOptions = [
  { value: "EET", label: "EET" },
  { value: "EMT", label: "EMT" },
  { value: "EPT", label: "EPT" },
  { value: "TPT", label: "TPT" },
  { value: "COVIP", label: "COVIP" },
];

export const filterProviderConfigsSchema = z.object({
  client_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  is_client_config: z.string().optional(),
  connector_config_id: z.string().nullable().optional(),
  file_pattern_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  source_folder_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  code_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  extract_type_contains: z.string().nullable().optional(),
  transform_type_contains: z.string().nullable().optional(),
  load_type_contains: z.string().nullable().optional(),
  priority: z.string().nullable().optional(),
  last_computed_date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  last_computed_date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export type FilterProviderConfigsSchema = z.infer<
  typeof filterProviderConfigsSchema
>;

const providerConfigDepFields: readonly DepField<FilterProviderConfigsSchema>[] =
  [
    {
      name: "code_contains",
      key: "code",
      extract: "text",
      bucket: "equals",
    },
    {
      name: "client_contains",
      key: "client",
      extract: "text",
      bucket: "equals",
    },
    {
      name: "file_pattern_contains",
      key: "file_pattern",
      extract: "text",
      bucket: "equals",
    },
    {
      name: "source_folder_contains",
      key: "source_folder",
      extract: "text",
      bucket: "equals",
    },
  ];

export const fields: Field[] = [
  {
    name: "client_contains",
    label: "Client",
    type: "asyncAutocomplete",
    base_url: "etl-quantilia/provider_configs",
    column_name: "client",
    filters: makeFiltersBuilder<FilterProviderConfigsSchema>(
      providerConfigDepFields,
      "client_contains",
    ),
  },
  {
    name: "connector_config_id",
    label: "Connector Config",
    type: "input",
    input_type: "text",
  },
  {
    name: "file_pattern_contains",
    label: "File Pattern",
    type: "asyncAutocomplete",
    base_url: "etl-quantilia/provider_configs",
    column_name: "file_pattern",
    filters: makeFiltersBuilder<FilterProviderConfigsSchema>(
      providerConfigDepFields,
      "file_pattern_contains",
    ),
  },
  {
    name: "source_folder_contains",
    label: "Source Folder",
    type: "asyncAutocomplete",
    base_url: "etl-quantilia/provider_configs",
    column_name: "source_folder",
    filters: makeFiltersBuilder<FilterProviderConfigsSchema>(
      providerConfigDepFields,
      "source_folder_contains",
    ),
  },
  {
    name: "code_contains",
    label: "Code",
    type: "asyncAutocomplete",
    base_url: "etl-quantilia/provider_configs",
    column_name: "code",
    filters: makeFiltersBuilder<FilterProviderConfigsSchema>(
      providerConfigDepFields,
      "code_contains",
    ),
  },
  {
    name: "extract_type_contains",
    label: "Extract Type",
    type: "select",
    useTranslation: false,
    options: extractTypeOptions,
  },
  {
    name: "transform_type_contains",
    label: "Transform Type",
    type: "select",
    useTranslation: false,
    options: transformTypeOptions,
  },
  {
    name: "load_type_contains",
    label: "Load Type",
    type: "select",
    useTranslation: false,
    options: loadTypeOptions,
  },
  {
    name: "priority",
    label: "Priority",
    type: "input",
    input_type: "text",
    allowZero: true,
  },
  {
    name: "is_client_config",
    label: "Client Config",
    type: "select",
    useTranslation: false,
    options: [
      { value: "true", label: "Yes" },
      { value: "false", label: "No" },
    ],
  },
];

export const client = "qtl - etl";

export function FilterProviderConfigs() {
  return (
    <FilterTable
      client={client}
      schema={filterProviderConfigsSchema}
      tableName="provider_configs"
      fields={fields}
      columns={2}
    />
  );
}
